import React, { useState } from 'react';
import { Star, StarOff, X } from 'lucide-react';

interface FeedbackPromptProps {
  onSubmit: (feedback: { rating: number; comment: string; planName: string }) => void;
  onClose: () => void;
  message: string;
}

export default function FeedbackPrompt({ onSubmit, onClose, message }: FeedbackPromptProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [planName, setPlanName] = useState(`Plano de Conteúdo - ${new Date().toLocaleDateString('pt-BR')}`);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Por favor, selecione uma classificação');
      return;
    }

    if (!comment.trim()) {
      setError('Por favor, deixe um comentário');
      return;
    }

    if (!planName.trim()) {
      setError('Por favor, dê um nome ao seu plano');
      return;
    }

    onSubmit({ rating, comment, planName });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-lg w-full mx-4 p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <span className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
            </span>
            Sua opinião é importante!
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <p className="text-gray-600 mb-6">{message}</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Plan Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome do Plano
            </label>
            <input
              type="text"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              placeholder="Digite um nome para seu plano"
            />
          </div>

          {/* Rating Stars */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Como você avalia este plano?
            </label>
            <div className="flex space-x-2 justify-center bg-gray-50 p-4 rounded-lg">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRating(value)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  {value <= rating ? (
                    <Star className="h-8 w-8 text-yellow-400 fill-current" />
                  ) : (
                    <StarOff className="h-8 w-8 text-gray-300" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Comment Box */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seus comentários
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              rows={4}
              placeholder="O que você achou do plano? Suas sugestões nos ajudam a melhorar."
            />
          </div>

          {error && (
            <p className="text-red-600 text-sm bg-red-50 p-2 rounded-lg border border-red-100">{error}</p>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-colors shadow-sm"
            >
              Enviar Feedback
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}